import React, { useState, useEffect, useRef } from 'react';
import { getDrafts, getDraftCounts, autoGenerateDrafts, updateDraft, approveDraft, rejectDraft, restoreDraft, deleteDraft, deleteDraftsBulk } from '../api';
import { safeErr } from '../App';

const STATUS_PILL = { pending:'pill-amber', approved:'pill-blue', rejected:'pill-gray', sent:'pill-green' };

/* Sandboxed preview — shows exactly what the recipient sees (clickable
   unsubscribe link etc.) without ever executing scripts. */
function HtmlPreview({ html }) {
  const ref = useRef(null);
  useEffect(() => {
    const iframe = ref.current;
    if (!iframe) return;
    const doc = iframe.contentDocument || iframe.contentWindow?.document;
    if (!doc) return;
    const safe = (html||'').replace(/<script[\s\S]*?<\/script>/gi,'')
                    .replace(/\son\w+\s*=\s*"[^"]*"/gi,'')
                    .replace(/\son\w+\s*=\s*'[^']*'/gi,'');
    const full = `<!DOCTYPE html><html><head><meta charset="utf-8">
<style>body{font-family:-apple-system,BlinkMacSystemFont,"Segoe UI",Arial,sans-serif;
  font-size:13px;line-height:1.7;color:#111827;margin:0;padding:14px;word-wrap:break-word;}
a{color:#2563EB}</style></head><body>${safe}</body></html>`;
    doc.open('text/html','replace'); doc.write(full); doc.close();
    const resize = () => {
      try {
        const h = doc.documentElement.scrollHeight || doc.body.scrollHeight;
        if (h > 30) iframe.style.height = (h + 24) + 'px';
      } catch(e) {}
    };
    [80,300].forEach(ms => setTimeout(resize, ms));
  }, [html]);
  return <iframe ref={ref} title="preview" sandbox="allow-same-origin" scrolling="no"
    style={{width:'100%',minHeight:160,border:'1px solid var(--border)',borderRadius:'var(--r-sm)',display:'block',background:'#fff'}}/>;
}

export default function Drafts({ showToast }) {
  const [drafts,  setDrafts]  = useState([]);
  const [counts,  setCounts]  = useState({pending:0,approved:0,rejected:0,sent:0});
  const [status,  setStatus]  = useState('pending');
  const [loading, setLoad]    = useState(true);
  const [open,    setOpen]    = useState({});
  const [edits,   setEdits]   = useState({});
  const [busy,    setBusy]    = useState({});
  const [generating, setGen]  = useState(false);
  const [batchSize,  setBatchSize] = useState(20);
  const [previewing, setPreviewing] = useState({}); // draftId -> bool
  const [clearing, setClearing] = useState(false);
  const generatingRef = useRef(false); // hard lock — closes the race window before React re-renders the disabled button

  const load = () => {
    setLoad(true);
    Promise.all([getDrafts({status}), getDraftCounts()])
      .then(([d,c]) => { setDrafts(d.data); setCounts(c.data); })
      .finally(()=>setLoad(false));
  };
  useEffect(load, [status]);

  const toggle = d => setOpen(p=>({...p,[d.id]:!p[d.id]}));

  const edit = (id, field, value) => setEdits(p=>({...p,[id]:{...p[id], [field]:value}}));
  const fieldVal = (d, field) => edits[d.id]?.[field] !== undefined ? edits[d.id][field] : d[field];

  const saveEdit = async d => {
    const changed = edits[d.id];
    if (!changed) return;
    setBusy(p=>({...p,[d.id]:true}));
    try {
      await updateDraft(d.id, changed);
      showToast('Draft updated');
      setEdits(p=>{ const n={...p}; delete n[d.id]; return n; });
      load();
    } catch(e) { showToast(safeErr(e,'Failed to save'),'error'); }
    finally { setBusy(p=>({...p,[d.id]:false})); }
  };

  const doApprove = async d => {
    if (!window.confirm(`Send this email to ${d.to_name||d.to_email} now?`)) return;
    setBusy(p=>({...p,[d.id]:true}));
    try {
      await approveDraft(d.id);
      showToast(`✓ Sent to ${d.to_email}`);
      load();
    } catch(e) { showToast(safeErr(e,'Failed to send'),'error'); }
    finally { setBusy(p=>({...p,[d.id]:false})); }
  };

  const doReject = async d => {
    setBusy(p=>({...p,[d.id]:true}));
    try {
      await rejectDraft(d.id);
      showToast('Draft rejected');
      load();
    } catch(e) { showToast(safeErr(e,'Failed to reject'),'error'); }
    finally { setBusy(p=>({...p,[d.id]:false})); }
  };

  const doRestore = async d => {
    setBusy(p=>({...p,[d.id]:true}));
    try {
      await restoreDraft(d.id);
      showToast('Moved back to pending');
      load();
    } catch(e) { showToast(safeErr(e,'Failed to restore'),'error'); }
    finally { setBusy(p=>({...p,[d.id]:false})); }
  };

  const doDelete = async d => {
    if (!window.confirm('Delete this draft permanently?')) return;
    setBusy(p=>({...p,[d.id]:true}));
    try {
      await deleteDraft(d.id);
      showToast('Draft deleted');
      load();
    } catch(e) { showToast(safeErr(e,'Failed to delete'),'error'); }
    finally { setBusy(p=>({...p,[d.id]:false})); }
  };

  const doGenerate = async () => {
    if (generatingRef.current || counts.pending > 0) return;
    generatingRef.current = true;
    setGen(true);
    try {
      const r = await autoGenerateDrafts(batchSize);
      showToast(`✓ Generated ${r.data.created} new draft${r.data.created===1?'':'s'}`);
      setStatus('pending');
      load();
    } catch(e) { showToast(safeErr(e,'Generation failed'),'error'); }
    finally { generatingRef.current = false; setGen(false); }
  };

  const doClearAll = async () => {
    const count = counts[status] || 0;
    if (!count) return;
    if (!window.confirm(`Permanently delete all ${count} ${status} draft${count===1?'':'s'}?`)) return;
    setClearing(true);
    try {
      const r = await deleteDraftsBulk(status);
      showToast(`✓ Deleted ${r.data.deleted} draft${r.data.deleted===1?'':'s'}`);
      load();
    } catch(e) { showToast(safeErr(e,'Failed to delete drafts'),'error'); }
    finally { setClearing(false); }
  };

  const TABS = [
    ['pending','Pending', counts.pending],
    ['approved','Approved', counts.approved],
    ['sent','Sent', counts.sent],
    ['rejected','Rejected', counts.rejected],
  ];

  return (
    <>
      <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:16,flexWrap:'wrap',gap:10}}>
        <div style={{display:'flex',gap:6}}>
          {TABS.map(([key,label,count])=>(
            <button key={key} onClick={()=>setStatus(key)} className="btn btn-sm"
              style={status===key?{background:'var(--blue)',color:'#fff',borderColor:'var(--blue)'}:{}}>
              {label} {count>0 && <span style={{marginLeft:5,opacity:.85}}>({count})</span>}
            </button>
          ))}
        </div>
        <div style={{display:'flex',gap:8,alignItems:'center'}}>
          {(status==='pending' || status==='rejected') && counts[status] > 0 && (
            <button className="btn btn-danger btn-sm" disabled={clearing} onClick={doClearAll}>
              {clearing ? <><span className="spinner spinner-sm"/>Deleting…</> : `🗑 Delete all ${status}`}
            </button>
          )}
          <select className="form-select" style={{width:90}} value={batchSize} onChange={e=>setBatchSize(Number(e.target.value))}>
            {[5,10,20].map(n=><option key={n} value={n}>{n} new</option>)}
          </select>
          <button className="btn btn-primary btn-sm" disabled={generating || counts.pending > 0} onClick={doGenerate}
            title={counts.pending > 0 ? 'Approve or reject pending drafts before generating more' : ''}>
            {generating ? <><span className="spinner spinner-sm"/>Researching…</> : '✦ Generate drafts'}
          </button>
        </div>
      </div>

      <div className="alert alert-info" style={{marginBottom:14,fontSize:12}}>
        Drafts are researched from each contact's company domain and written by AI, but <strong>nothing sends until you approve it here</strong>.
      </div>

      {loading ? (
        <div className="loading-center"><div className="spinner spinner-lg"/></div>
      ) : drafts.length===0 ? (
        <div className="empty-state">
          <div className="empty-icon">✦</div>
          <div className="empty-title">No {status} drafts</div>
          <div className="empty-desc">
            {status==='pending'
              ? 'Click "Generate drafts" to research a batch of your Client contacts and draft personalized emails.'
              : `No drafts are currently ${status}.`}
          </div>
        </div>
      ) : drafts.map(d => (
        <div key={d.id} className="card" style={{marginBottom:10}}>
          <div style={{padding:'12px 16px',display:'flex',alignItems:'center',gap:10,cursor:'pointer'}}
            onClick={()=>toggle(d)}>
            <div className="avatar av-sm av-blue" style={{flexShrink:0}}>
              {(d.to_name||d.to_email).split(' ').map(w=>w[0]).join('').slice(0,2).toUpperCase()}
            </div>
            <div style={{flex:1,minWidth:0}}>
              <div style={{fontWeight:600,fontSize:13}}>{d.to_name || d.to_email} {d.company_domain && <span style={{color:'var(--text-3)',fontWeight:400}}>· {d.company_domain}</span>}</div>
              <div style={{fontSize:12,color:'var(--text-2)',overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>{d.subject}</div>
            </div>
            <span className={`pill ${STATUS_PILL[d.status]||'pill-gray'}`} style={{fontSize:10}}>{d.status}</span>
            <span style={{color:'var(--text-3)',fontSize:13,transition:'transform .2s',
              display:'inline-block',transform:open[d.id]?'rotate(180deg)':''}}>▾</span>
          </div>

          {open[d.id] && (
            <div style={{padding:'0 16px 16px',borderTop:'1px solid var(--border)'}}>
              {d.research_summary && (
                <div style={{marginTop:12,padding:'10px 12px',background:'var(--surface2)',
                  borderRadius:'var(--r-sm)',fontSize:12,color:'var(--text-2)',lineHeight:1.6}}>
                  <strong style={{color:'var(--text)'}}>Research:</strong> {d.research_summary}
                </div>
              )}
              {!d.research_summary && (
                <div style={{marginTop:12,fontSize:11,color:'var(--text-3)'}}>
                  No company research available (personal email domain or site unreachable) — kept general.
                </div>
              )}

              <div className="form-group" style={{marginTop:12}}>
                <label className="form-label">Subject</label>
                <input className="form-input" value={fieldVal(d,'subject')}
                  disabled={d.status!=='pending'}
                  onChange={e=>edit(d.id,'subject',e.target.value)}/>
              </div>
              <div className="form-group">
                <div style={{display:'flex',justifyContent:'space-between',alignItems:'center'}}>
                  <label className="form-label" style={{marginBottom:0}}>Body</label>
                  <button className="btn btn-sm" style={{padding:'2px 9px',fontSize:11}}
                    onClick={()=>setPreviewing(p=>({...p,[d.id]:!p[d.id]}))}>
                    {previewing[d.id] ? '✎ Edit' : '👁 Preview (as recipient sees it)'}
                  </button>
                </div>
                {previewing[d.id] ? (
                  <div style={{marginTop:6}}>
                    {edits[d.id] && (
                      <div style={{fontSize:11,color:'var(--amber)',marginBottom:6}}>
                        Showing last saved version — save your edits to update the preview.
                      </div>
                    )}
                    <HtmlPreview html={d.body_html}/>
                  </div>
                ) : (
                  <textarea className="form-textarea" style={{minHeight:220,fontFamily:'inherit',fontSize:13,lineHeight:1.7}}
                    value={fieldVal(d,'body')}
                    disabled={d.status!=='pending'}
                    onChange={e=>edit(d.id,'body',e.target.value)}/>
                )}
              </div>

              {d.status==='pending' && (
                <div style={{display:'flex',gap:8,marginTop:4}}>
                  {edits[d.id] && (
                    <button className="btn btn-sm" disabled={busy[d.id]} onClick={()=>saveEdit(d)}>
                      Save changes
                    </button>
                  )}
                  <button className="btn btn-primary btn-sm" disabled={busy[d.id]} onClick={()=>doApprove(d)}>
                    {busy[d.id] ? 'Sending…' : '✓ Approve & send'}
                  </button>
                  <button className="btn btn-danger btn-sm" disabled={busy[d.id]} onClick={()=>doReject(d)}>
                    ✕ Reject
                  </button>
                </div>
              )}

              {d.status==='rejected' && (
                <div style={{display:'flex',gap:8,marginTop:4}}>
                  <button className="btn btn-sm" disabled={busy[d.id]} onClick={()=>doRestore(d)}>
                    ↩ Move to pending
                  </button>
                  <button className="btn btn-danger btn-sm" disabled={busy[d.id]} onClick={()=>doDelete(d)}>
                    🗑 Delete
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      ))}
    </>
  );
}
